import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import { CreditCard, PhoneCall } from 'lucide-react';

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const subscription = user?.subscription;
  const hasActiveSubscription = subscription?.status === 'ACTIVE';

  const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }) : 'Not set';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasActiveSubscription) {
    const statusMessage = subscription?.status === 'EXPIRED'
      ? 'Your subscription is ended please contact support.'
      : 'Please contact support you dont ahve any subscription.';

    return (
      <Layout>
        <div className="mx-auto flex min-h-[calc(100vh-10rem)] max-w-4xl items-center justify-center">
          <div className="w-full rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-8">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
              <CreditCard className="h-8 w-8" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-slate-950">Subscription required to continue</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
              {statusMessage}
            </p>

            <div className="mt-6 grid gap-4 rounded-[28px] border border-slate-200 bg-slate-50 p-5 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Account</p>
                <p className="mt-2 text-lg font-bold text-slate-950">{user?.name}</p>
                <p className="mt-1 text-sm text-slate-600">{user?.email}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Current subscription</p>
                {subscription ? (
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    <p><span className="font-semibold text-slate-950">Plan:</span> {subscription.plan}</p>
                    <p><span className="font-semibold text-slate-950">Status:</span> {subscription.status}</p>
                    <p><span className="font-semibold text-slate-950">Start:</span> {formatDate(subscription.startDate)}</p>
                    <p><span className="font-semibold text-slate-950">End:</span> {formatDate(subscription.endDate)}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm font-medium text-amber-700">No subscription is assigned to this account.</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                className="h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800"
                onClick={() => window.open('https://wa.me/919067611076', '_blank')}
              >
                <PhoneCall className="mr-2 h-4 w-4" />
                Contact support
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return children;
};
